import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import SalaryBatchesPanel from "@/components/app/SalaryBatchesPanel";
import { Loading, Empty, Row, WorkflowBadge } from "./_shared";
import { ReceiptBadge, ReceiptConfirmBlock } from "./PayrollReceipts";

export default function SalaryRequests({ staff }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("individual"); // "individual" | "bulk"
  const staffName = (id) => staff.find((x) => x.id === id)?.full_name || id;

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/hr/salary-requests")).data.items || []); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const STATUS_LABELS = {
    submitted: "Submitted (awaiting Finance)", finance_review: "Under Finance Review",
    finance_cleared: "Cleared by Finance", finance_rejected: "Rejected by Finance",
    admin_review: "With Admin", approved: "Approved", rejected: "Rejected", paid: "Paid",
  };

  const transition = async (status, comment = "") => {
    try {
      await api.post(`/hr/salary-requests/${selected.id}/transition`, { status, comment });
      toast.success(`Moved to ${STATUS_LABELS[status]}`);
      setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canFinance = hasMinRole(user, "manager");
  const canAdmin = hasMinRole(user, "admin");

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-base font-semibold">Payroll / Salary Workflow</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="salary-view-toggle">
            <button
              type="button"
              onClick={() => setView("individual")}
              data-testid="salary-view-individual"
              className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
                view === "individual" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Individual requests
            </button>
            <button
              type="button"
              onClick={() => setView("bulk")}
              data-testid="salary-view-bulk"
              className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
                view === "bulk" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Bulk batches
            </button>
          </div>
          {view === "individual" && (
            <SalaryRequestDialog staff={staff} open={open} onOpenChange={setOpen} onCreated={load} />
          )}
        </div>
      </div>

      {view === "bulk" ? (
        <SalaryBatchesPanel
          mode="hr"
          title="Bulk salary batches"
          description="Create payroll batches across departments, projects or contract types. Each batch routes to Finance for clearance, then to the CEO for final approval."
        />
      ) : (
      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : items.length === 0 ? <Empty text="No salary requests yet." /> : (
          <>
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr><th className="px-5 py-3">Employee</th><th className="px-5 py-3">Period</th>
                    <th className="px-5 py-3">Gross</th><th className="px-5 py-3">Net</th>
                    <th className="px-5 py-3">Status</th><th className="px-5 py-3">Receipt</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} className="text-sm cursor-pointer hover:bg-slate-50" onClick={() => setSelected(r)}>
                    <td className="px-5 py-3 font-semibold text-slate-900">{staffName(r.staff_id)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{r.period_month}</td>
                    <td className="px-5 py-3 font-mono-alt tabular-nums">{formatCurrency(r.gross_amount)}</td>
                    <td className="px-5 py-3 font-mono-alt tabular-nums font-semibold">{formatCurrency(r.net_amount)}</td>
                    <td className="px-5 py-3"><WorkflowBadge status={r.status} /></td>
                    <td className="px-5 py-3"><ReceiptBadge req={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-slate-100 md:hidden">
              {items.map((r) => (
                <li key={r.id} className="p-4 cursor-pointer" onClick={() => setSelected(r)}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900">{staffName(r.staff_id)}</span>
                    <WorkflowBadge status={r.status} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{r.period_month} · Gross {formatCurrency(r.gross_amount)} → Net {formatCurrency(r.net_amount)}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Salary Request — {selected.period_month}</DialogTitle>
                <DialogDescription>Employee {staffName(selected.staff_id)} · Current status: <strong>{STATUS_LABELS[selected.status]}</strong></DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Row label="Gross">{formatCurrency(selected.gross_amount)}</Row>
                <Row label="Additions">{formatCurrency(selected.additions || 0)}</Row>
                <Row label="Deductions">{formatCurrency(selected.deductions || 0)}</Row>
                <Row label="Net">{formatCurrency(selected.net_amount)}</Row>
              </div>
              <div>
                <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-2">Workflow history</div>
                <ul className="space-y-1 text-xs text-slate-600 max-h-40 overflow-y-auto">
                  {(selected.workflow || []).map((w, i) => (
                    <li key={i} className="rounded bg-slate-50 px-2 py-1">
                      <span className="font-semibold text-slate-900">{STATUS_LABELS[w.status] || w.status}</span>
                      {" "}· {w.by} · {new Date(w.at).toLocaleString()}
                      {w.comment && <div className="mt-0.5 text-slate-500">{w.comment}</div>}
                    </li>
                  ))}
                </ul>
              </div>

              {selected.status === "paid" && (
                <ReceiptConfirmBlock
                  request={selected}
                  onConfirmed={() => { setSelected(null); load(); }}
                />
              )}
              <DialogFooter className="flex-wrap gap-2">
                {selected.status === "submitted" && canFinance && (
                  <>
                    <Button className="btn-primary-blue" onClick={() => transition("finance_cleared", "Cleared by Finance Officer")}>Clear (Finance)</Button>
                    <Button variant="destructive" onClick={() => transition("finance_rejected", "Rejected by Finance")}>Reject (Finance)</Button>
                  </>
                )}
                {selected.status === "finance_cleared" && canAdmin && (
                  <>
                    <Button className="btn-primary-blue" onClick={() => transition("approved", "Final approval granted")}>Approve (Admin)</Button>
                    <Button variant="destructive" onClick={() => transition("rejected", "Rejected by Admin")}>Reject (Admin)</Button>
                  </>
                )}
                {selected.status === "approved" && canAdmin && (
                  <Button className="btn-primary-blue" onClick={() => transition("paid", "Payment executed")}>Mark as Paid</Button>
                )}
                <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SalaryRequestDialog({ staff, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ staff_id: "", period_month: "", gross_amount: 0, additions: 0, deductions: 0, notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, gross_amount: Number(form.gross_amount), additions: Number(form.additions), deductions: Number(form.deductions) };
      await api.post("/hr/salary-requests", payload);
      toast.success("Salary request submitted to Finance");
      onOpenChange(false); setForm({ staff_id: "", period_month: "", gross_amount: 0, additions: 0, deductions: 0, notes: "" }); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild><Button className="btn-primary-blue"><Plus size={14} /> New Salary Request</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Salary Request</DialogTitle>
          <DialogDescription>Will be routed Finance Officer → Admin for approval.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Employee *</Label>
            <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select employee…" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.employee_code || "—"})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Period Month (YYYY-MM) *</Label><Input required placeholder="2026-04" value={form.period_month} onChange={(e) => setForm({ ...form, period_month: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Gross (USD) *</Label><Input type="number" required value={form.gross_amount} onChange={(e) => setForm({ ...form, gross_amount: e.target.value })} /></div>
            <div><Label>Additions</Label><Input type="number" value={form.additions} onChange={(e) => setForm({ ...form, additions: e.target.value })} /></div>
            <div><Label>Deductions</Label><Input type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <DialogFooter><Button type="submit" disabled={busy || !form.staff_id} className="btn-primary-blue">{busy ? "Submitting…" : "Submit for Finance Review"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
